// Thin API client for the Vennio REST API. Attaches the current Supabase JWT as
// a Bearer token (the API's requireSecretKey() accepts JWT sessions). Absolute
// base URL — when.vennio.app talks cross-origin to api.vennio.app.
import { supabase } from './supabase';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export class ApiError extends Error {
  constructor(message, { status, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body || {};
    this.detail = this.body.detail || null;
  }
}

async function authHeader() {
  if (!supabase) return {};
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * apiFetch(path, { method, body, auth })
 * - path: API path beginning with '/v1/...'
 * - auth: attach the JWT (default true). Set false for public endpoints.
 * Returns parsed JSON (or null for 204). Throws ApiError on non-2xx.
 */
export async function apiFetch(path, { method = 'GET', body, auth = true, headers = {} } = {}) {
  const h = { 'Content-Type': 'application/json', ...headers };
  if (auth) Object.assign(h, await authHeader());

  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: h,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 204) return null;

  let payload = null;
  try { payload = await res.json(); } catch { /* non-JSON body */ }

  if (!res.ok) {
    const message = payload?.message || payload?.detail || payload?.error || `Request failed (${res.status})`;
    throw new ApiError(message, { status: res.status, body: payload });
  }
  return payload;
}

export { API_URL };
